import React, { useState } from 'react';
import { style } from 'typestyle';
import { colorDarkBlueGrey, colorGreyAccent, colorWhite, rebrand } from '../../../theme/color';
import { center, flexRoot } from 'csstips';
import { spacing } from '../../../theme/spacing';
import { zModal } from '../../../theme/z';
import { important, px } from 'csx';
import { AddRemoveCompat } from '../../compat/add-remove-compat';
import { FavouriteButtonCompat } from '../../compat/favourite-button-compat';
import { PlayerBarNowPlaying } from '../../player-bar-now-playing';
import { AddToPlaylistButton } from '../atoms/button/AddToPlaylistButton';
import { ImageWithPlayBtn } from '../atoms/ImageWithPlayBtn';
import { ComponentProps, inject, deps, observer } from '../../../lib/component';
import { isEmpty } from '../utilities/objects';
import { borderRadius } from '../../../theme/border';
import { TrackSchemaWithUsage } from '../../../modules/page/checkout/model';
import { isFinePointer, mediaSmallTablet, mediaUpToSmallTablet } from '../utilities/mediaQueries';
import { TrackInfoButton } from '../atoms/button/TrackInfoButton';
import { SubscriptionLabel } from '../atoms/SubscriptionLabel';
import { Tooltip } from '../atoms/Tooltip';
import { InfoOutline } from '../atoms/icons/InfoOutline';

interface TrackFeatureCardProps extends ComponentProps {
  track: TrackSchemaWithUsage;
  onClickFavourite?: () => void;
  onClickLink?: (event: React.MouseEvent<any>) => void;
  hideTrackInfoBtn?: boolean;
}

const styles = {
  wrapper: style({
    position: 'relative',
    display: 'flex',
    flexShrink: 0,
    background: rebrand.contrast[50].toHexString(),
    borderRadius: borderRadius.MEDIUM,
    overflow: 'hidden',
  }),
  trackImage: style(
    {
      width: px(128),
      height: px(128),
      flexShrink: 0,
    },
    mediaSmallTablet({
      width: px(118),
      height: px(118),
    })
  ),
  content: style(
    {
      padding: spacing.DEFAULT,
      flex: 1,
    },
    mediaSmallTablet(flexRoot, center, {})
  ),
  trackDetails: style(
    {
      padding: important(0),
      maxWidth: 'none',
    },
    mediaSmallTablet({
      marginRight: spacing.DEFAULT,
    })
  ),
  actionButtons: style(
    flexRoot,
    center,
    {
      gap: spacing.XSMALL,
      marginLeft: 'auto',
      flexShrink: 0,
    },
    mediaUpToSmallTablet({ marginTop: spacing.DEFAULT, flexWrap: 'wrap' }),
    mediaSmallTablet({ justifyContent: 'flex-end' })
  ),
  addToPlaylistButton: style({ width: px(32), height: px(32) }),
  addToPlaylistTooltip: style({ zIndex: zModal }),
  trackInfoBtn: style({
    backgroundColor: colorGreyAccent.toString(),
    $nest: {
      '&:hover': {
        backgroundColor: colorDarkBlueGrey.toString(),
      },
    },
  }),
  notifyBtn: style({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginLeft: 'auto',
    padding: ' 0 10px 0 8px',
  }),
};

export const TrackFeatureCard: React.FC<TrackFeatureCardProps> = inject(deps)(
  observer(
    ({
      track,
      onClickFavourite,
      onClickLink,
      hideTrackInfoBtn = false,
      controller,
      model: {
        user,
        player: { currentTrack, play },
      },
    }) => {
      const [hidePlayBtn, setHidePlayBtn] = useState(true);
      const isLoggedIn = !isEmpty(user.user);
      const trackIsAvailable = track.is_available_in_territory === null ? true : track.is_available_in_territory;

      // Is the track currently playing?
      const isCurrent = currentTrack && track.identity === currentTrack.identity;
      const isCurrentPlaying = play && isCurrent;

      const handleClickLink = (event: React.MouseEvent<any>): void => {
        if (typeof onClickLink === 'function') {
          onClickLink(event);
        }

        controller.ui.handleClickLink(event);
      };

      // Mouse enter and leave events should only be triggered if not using a touch device
      const handleMouseEnter = (): void => {
        if (isFinePointer.matches) {
          setHidePlayBtn(false);
        }
      };

      const handleMouseLeave = (): void => {
        if (isFinePointer.matches) {
          setHidePlayBtn(true);
        }
      };

      return (
        <div
          className={styles.wrapper}
          data-test-track-feature-card={track.identity}
          data-test-is_available_in_territory={trackIsAvailable}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <SubscriptionLabel included={track.is_stock_music} />
          <ImageWithPlayBtn
            track={track}
            trackImageClassname={styles.trackImage}
            hidePlayBtn={hidePlayBtn && !isCurrentPlaying}
          />

          <div className={styles.content}>
            <PlayerBarNowPlaying
              analytics={controller.analytics}
              model={{ track }}
              onClickLink={handleClickLink}
              className={styles.trackDetails}
            />

            <div className={styles.actionButtons}>
              {trackIsAvailable && (
                <>
                  <span>
                    <AddRemoveCompat track={track} size={32} />
                  </span>
                  <div onClick={!isLoggedIn ? onClickFavourite : null}>
                    <FavouriteButtonCompat track={track} size={32} />
                  </div>
                  {isLoggedIn && (
                    <AddToPlaylistButton
                      track={track}
                      className={styles.addToPlaylistButton}
                      tooltipClassName={styles.addToPlaylistTooltip}
                    />
                  )}
                </>
              )}
              {!trackIsAvailable && (
                <Tooltip positions={['top']} content={<span> Not available in your region</span>}>
                  <InfoOutline color={colorWhite.toHexString()} />
                </Tooltip>
              )}
              {!hideTrackInfoBtn && <TrackInfoButton track={track} />}
            </div>
          </div>
        </div>
      );
    }
  )
);
